import * as THREE from "three";
//import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { EffectComposer } from "@/services/color-engine/dioxygen/three-js-post-processing-pipeline.module.js";
import RenderingPipeline from "../rendering/RenderingPipeline";
import { Ao } from "@/services";
import { useUIStore } from "@/stores/uiStore";

declare global {
  interface Window {
    renderer?: THREE.WebGLRenderer;
  }
}

export class RenderManager {
  private static instance: RenderManager;

  static MINIMUM_FPS = 60;

  renderNeedsUpdate = true;
  isAddShadow = false;
  isMouseDown = false;
  shadowsEnabled: boolean = true;
  isCheckFPS = false;
  FPStimeout = null;
  fps = 144;
  frameNumber = 0;
  renderingPipeline: RenderingPipeline | null = null;

  // Store renderer and effect composer locally
  private renderer: THREE.WebGLRenderer | null = null;
  private effectComposer: any; //EffectComposer = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): RenderManager {
    if (!RenderManager.instance) {
      RenderManager.instance = new RenderManager();
    }
    return RenderManager.instance;
  }

  setRenderingPipeline = (pipeline: any) => {
    this.renderingPipeline = pipeline;
  };

  async initRenderer(
    width: number,
    height: number,
    shadowsEnabled?: boolean
  ): Promise<void> {
    try {
      this.renderer = new THREE.WebGLRenderer({
        antialias: false,
        alpha: true
      });

      // Use provided shadowsEnabled or default to current value
      const shadows =
        shadowsEnabled !== undefined ? shadowsEnabled : this.shadowsEnabled;
      this.setupRenderer(this.renderer, width, height, shadows);
      await this.setupEffectComposer(this.renderer, width, height);

      // Renderer is now stored locally, no need to set in external context
    } catch (error) {
      console.error("Failed to initialize renderer:", error);
      throw error;
    }
  }

  private setupRenderer(
    renderer: THREE.WebGLRenderer,
    width: number,
    height: number,
    shadowsEnabled: boolean = true
  ): void {
    renderer.sortObjects = false;
    renderer.shadowMap.enabled = shadowsEnabled;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    this.shadowsEnabled = shadowsEnabled;
    // Clamp device pixel ratio to reduce GPU load on high-DPI displays
    const MAX_DPR = 1.75;
    const clampedDpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    renderer.setPixelRatio(clampedDpr);
    renderer.setSize(width, height);
    // Remove global window assignment to avoid conflicts
  }

  private async setupEffectComposer(
    renderer: THREE.WebGLRenderer,
    width: number,
    height: number
  ): Promise<void> {
    if (!renderer) return;

    try {
      this.effectComposer = new EffectComposer(renderer);
      this.effectComposer.setSize(width, height);
      // Effect composer is now stored locally, no need to set in external context
    } catch (error) {
      console.warn("Failed to setup effect composer:", error);
    }
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    const uiStore = useUIStore.getState();
    if (
      this.renderingPipeline &&
      ((this.renderingPipeline as Ao).aoEnabled || uiStore.colorMode)
    ) {
      this.renderingPipeline.render();
    } else if (this.renderer) {
      this.renderer.render(scene, camera);
    }
  }

  onWindowResize(width: number, height: number): void {
    if (this.effectComposer) {
      this.effectComposer.setSize(width, height);
    }
    if (this.renderer) {
      this.renderer.setSize(width, height);
    }
    if (this.renderingPipeline) {
      this.renderingPipeline.updateSize(this.renderer);
    }
  }

  getRenderer(): THREE.WebGLRenderer | null {
    return this.renderer;
  }

  getEffectComposer(): typeof EffectComposer {
    return this.effectComposer;
  }

  setRenderNeedsUpdate(value: boolean): void {
    this.renderNeedsUpdate = value;
  }

  setShadowsEnabled(enabled: boolean): void {
    this.shadowsEnabled = enabled;
    if (this.renderer) {
      this.renderer.shadowMap.enabled = enabled;
      // Trigger a render update to see changes immediately
      this.renderNeedsUpdate = true;

      // If rendering pipeline is active, trigger its update too
      if (this.renderingPipeline) {
        this.renderingPipeline.setRenderNeedsUpdate(true);
      }
    }
  }

  getShadowsEnabled(): boolean {
    return this.shadowsEnabled;
  }
}
